import { useState, useEffect, useRef } from "react";
import { Title, Box, Group, Stack, Radio, Text, Grid, Progress } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { PageWithBreadcrumbs } from "../../components/layout/PageWithBreadcrumbs";
import { ContainerWhite } from "../../components/core/ContainerWhite/ContainerWhite";
import {
  AfaCombobox,
  AfaFieldDisplay,
  AfaDropzone,
  AfaTextarea,
  AfaCheckbox,
  AfaAnchor,
  AfaButton,
  AfaModal,
  AfaAlert,
  AfaSelect,
  AfaDatePickerInput,
  AfaTextInput,
  AfaLoader,
  AfaPageLoader,
} from "../../components/ui";
import { tramesesGroupedType, MesInfo } from "../../interface/trameses";
import DestinatarisEditable, { DestinatarisEditableRef } from "./destinataris";
import {
  convertToBase64,
  mesesTramesa,
  periocoidad,
  base64ToFile,
} from "../../utils/Utils";

const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB — seguro para Consumption Plan

const MAX_CHUNK_RETRIES = 3;
const MAX_SESSION_RETRIES = 2; // reintentos desde 0 si se aborta la sesión

async function uploadFileInChunks(
  file: any,
  itemId: any,
  listName: string,
  apiService: any,
) {
  let lastSessionError: any;

  for (let sessionAttempt = 1; sessionAttempt <= MAX_SESSION_RETRIES; sessionAttempt++) {
    try {
      // 1. Crear sesión
      const { sessionId } = await apiService.InitUploadSession({
        itemId,
        listName,
        fileName: "00_" + file.name,
        fileSize: file.size,
      });

      // 2. Enviar chunks binarios amb reintentos
      for (let start = 0; start < file.size; start += CHUNK_SIZE) {
        const end = Math.min(start + CHUNK_SIZE - 1, file.size - 1);
        const chunk = file.slice(start, end + 1);

        let lastError: any;
        for (let attempt = 1; attempt <= MAX_CHUNK_RETRIES; attempt++) {
          try {
            await apiService.UploadChunk(chunk, {
              "x-session-id": sessionId,
              "x-range-start": start,
              "x-range-end": end,
              "x-file-size": file.size,
            });
            lastError = null;
            break;
          } catch (error: any) {
            lastError = error;
            const httpStatus = error?.response?.status;
            // 5xx errors may leave the server session in a corrupted state — abort immediately,
            // do not retry (a retry of the same chunk could produce a corrupt file).
            if (httpStatus && httpStatus >= 500) break;
            if (attempt < MAX_CHUNK_RETRIES) {
              await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
            }
          }
        }

        if (lastError) {
          try { await apiService.AbortUploadSession(sessionId); } catch (_) {}
          throw new Error(
            `Error pujant el fitxer "${file.name}" (byte ${start}–${end}): ${lastError.message}`,
          );
        }
      }

      return; // subida completada con éxito
    } catch (error: any) {
      lastSessionError = error;
      if (sessionAttempt < MAX_SESSION_RETRIES) {
        // Esperar antes de reintentar desde 0 con una nueva sesión
        await new Promise((resolve) => setTimeout(resolve, sessionAttempt * 2000));
      }
    }
  }

  throw lastSessionError;
}

export function TramesesIniciarNova(props: any) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const tramesaValorInicial = location.state?.TipologiaTramesa;
  const isEntitatDefault = location.state?.DefaultEntity ?? false;

  // UI States
  const [tramitId, setTramitId] = useState<string | undefined>(id);
  const [checkTerms, setCheckTerms] = useState(false);

  const [tipoReferencia, setTipoReferencia] = useState<string>("");
  const [tipologiaTramesaEnable, setTipologiaTramesaEnable] =
    useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<
    Array<{
      file: File;
      name: string;
      type: "pdf" | "doc" | "xls" | "ppt" | "png" | "jpg" | "other";
    }>
  >([]);
  const [oldFiles, setOldFiles] = useState<
    Array<{
      file: File;
      name: string;
      type: "pdf" | "doc" | "xls" | "ppt" | "png" | "jpg" | "other";
    }>
  >([]);
  const [deletedFiles, setDeletedFiles] = useState<string[]>([]);
  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [cancelModalOpened, setCancelModalOpened] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null,
  );
  const destinatarisRef = useRef<DestinatarisEditableRef>(null);
  const [unsavedModalOpened, setUnsavedModalOpened] = useState(false);
  const [unsavedEmail, setUnsavedEmail] = useState("");

  // Trameses Data
  const [tramesesFilial, setTramesesFilial] = useState<
    { value: string; label: string }[]
  >([]);
  const [tramesesType, setTramesesTypes] = useState<
    {
      value: string;
      label: string;
      periodicity: string;
      startDate: string;
      initialMonth: string;
    }[]
  >([]);
  const [periodoTramesa, setPeriodoTramesa] = useState<
    { value: string; label: string }[]
  >([]);
  const [anyOptions, setAnyOptions] = useState<string[]>([]);
  const [tipologiaTramesa, setTipologiaTramesa] = useState<string>("");
  const [filialTramesa, setFilialTramesa] = useState<string>("");
  const [groups, setGroups] = useState<tramesesGroupedType[]>([]);
  const [infoInteres, setInfoInteres] = useState([] as any[]);
  const [currentPeriodicity, setCurrentPeriodicity] = useState<string>("");

  // Edit Mode
  const [editar, setEditar] = useState<boolean>(false);
  const [titleInit, setTitleInit] = useState<string>("Iniciar Nova Tramesa");
  const [tramesaData, setTramesaData] = useState<any>(null);
  const [initialPeriodValues, setInitialPeriodValues] = useState<{
    month: string;
    year: string;
  } | null>(null);

  // Form Data
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [getPeriodValue, setPeriodValue] = useState<string>("");
  const [getAnyValue, setAnyValue] = useState<string>("");
  const [getComentarisValue, setComentarisValue] = useState("");
  const [otherReceivers, setOtherReceivers] = useState<string[]>([]);

  // Loading States
  const [loadingTypes, setLoadingTypes] = useState<boolean>(true);
  const [loadingFilial, setLoadingFilial] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [loadingPage, setLoadingPage] = useState<boolean>(true);
  const [loadResponsePDF, setLoadResponse] = useState<boolean>(false);
  const [pdf, setPDF] = useState<string>("");
  const [codi, setCodi] = useState<string>("");

  const normalizePdfBase64 = (pdf: any): string => {
    if (!pdf) return "";
    if (typeof pdf === "string") {
      return pdf;
    }
    if (Array.isArray(pdf)) {
      return btoa(
        pdf.reduce((data, byte) => data + String.fromCharCode(byte), ""),
      );
    }

    return "";
  };

  const formRef = useRef<HTMLFormElement>(null);
  const [pendingReporting, setPendingReporting] = useState<string | null>(null);

  const handleBreadcrumbNavigation = async (to: string): Promise<boolean> => {
    setPendingNavigation(to);
    setCancelModalOpened(true);
    return false;
  };

  const getFileExtension = (filename: string): string | null => {
    const cleanName = filename.trim();

    const lastDotIndex = cleanName.lastIndexOf(".");
    if (lastDotIndex === -1 || lastDotIndex === cleanName.length - 1) {
      return null;
    }

    return cleanName.substring(lastDotIndex + 1).toLowerCase();
  };

  useEffect(() => {
    console.log("DEBUG location.state:", location.state);
    console.log("DEBUG TipusDoc:", location.state?.TipusDoc);
    console.log("DEBUG TipologiaTramesa:", location.state?.TipologiaTramesa);
    console.log("DEBUG mes:", location.state?.periodoTramesa);
    console.log("DEBUG any:", location.state?.anyOptions);
    if (location.state?.DefaultEntity) {
      setTipologiaTramesaEnable(location.state?.DefaultEntity ? true : false);
      setTipoReferencia("entitat");
      handleTramesReferenceChange("entitat");
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state?.fromUploadDocs) {
      setLoadResponse(true);
      setCodi(location.state.codi ?? "");
      setTramitId(id);
      const normalizedPdf = normalizePdfBase64(location.state.pdf);
      setPDF(normalizedPdf);
    }
  }, []);

  // Validation Effect
  useEffect(() => {
    const isValid =
      tipoReferencia !== "" &&
      tipologiaTramesa !== "" &&
      (currentPeriodicity === "SP" ||
        (getPeriodValue !== "" && getAnyValue !== ""));

    setIsFormValid(isValid);
  }, [
    tipoReferencia,
    tipologiaTramesa,
    currentPeriodicity,
    getPeriodValue,
    getAnyValue,
  ]);

  // Initial Load Effect
  useEffect(() => {
    const init = async () => {
      try {
        props.apiService
          .GetRelevantInformation("trameses", props.entity.lookupId)
          .then((info: any) => {
            setInfoInteres(info);
          });

        if (id != null) {
          // Modo edición
          const data = await props.apiService.GetTramesaById(
            id,
            props.entity.lookupId,
          );
          setTramesaData(data);
          setEditar(true);
          setTitleInit(`Editar Tramesa: ${data.guid}`);

          // Cargar datos del formulario
          if (data.formJson) {
            const formJsonParsed = JSON.parse(data.formJson);
            setGroups(formJsonParsed);
          }

          if (data.docs) {
            const files = data.docs.map((doc: any) => {
              //const extension = doc.title.split('.').pop()?.toLowerCase();
              const extension = getFileExtension(doc.title);
              let type:
                | "pdf"
                | "doc"
                | "xls"
                | "ppt"
                | "png"
                | "jpg"
                | "other" = "other";

              if (extension === "pdf") type = "pdf";
              else if (extension === "doc" || extension === "docx")
                type = "doc";
              else if (extension === "xls" || extension === "xlsx")
                type = "xls";
              else if (extension === "ppt" || extension === "pptx")
                type = "ppt";
              else if (extension === "png") type = "png";
              else if (extension === "jpg" || extension === "jpeg")
                type = "jpg";

              const file = base64ToFile(doc.content, doc.title);

              return { file, name: doc.title, type };
            });
            setOldFiles(files);
            setUploadedFiles(files);
          }

          if (data.otherReceivers) setOtherReceivers(data.otherReceivers);
          if (data.comments && data.comments.length > 0)
            setComentarisValue(data.comments[0].commentText);

          // Guardar los valores iniciales para aplicarlos después
          setInitialPeriodValues({
            month: data.referencePeriodMonth,
            year: data.referencePeriodYear,
          });

          // Determinar si es filial o entidad y cargar los datos correspondientes
          if (data.filial.lookupId !== -1) {
            setTipoReferencia("filial");
            setFilialTramesa(String(data.filial.lookupId));

            // Cargar opciones de filiales
            const fieldsFilial = await props.apiService.GetFilialsFromEntity(
              props.entity.lookupId,
            );
            setTramesesFilial(fieldsFilial);

            // Cargar tipos de tramesa para la filial
            const fieldsTramesa = await props.apiService.GetTramesesTypes(
              "filialId",
              data.filial.lookupId,
            );
            setTramesesTypes(fieldsTramesa);
            setLoadingTypes(false);
            setTipologiaTramesaEnable(true);

            // Establecer tipología después de cargar las opciones
            setTipologiaTramesa(String(data.tramesaType.lookupId));
          } else {
            setTipoReferencia("entitat");

            // Cargar tipos de tramesa para la entidad
            const fieldsTramesa = await props.apiService.GetTramesesTypes(
              "entityId",
              props.entity.lookupId,
            );
            setTramesesTypes(fieldsTramesa);
            setLoadingTypes(false);
            setTipologiaTramesaEnable(true);

            // Establecer tipología después de cargar las opciones
            setTipologiaTramesa(String(data.tramesaType.lookupId));
          }
        }

        const query = new URLSearchParams(location.search);

        const yearParam = query.get("year");
        const monthParam = query.get("month");
        const reportingParam = query.get("reporting");

        const rawFilialParam = query.get("filial");

        const filialIdFromUrl =
          rawFilialParam && /^\d+$/.test(rawFilialParam)
            ? rawFilialParam
            : null;

        if (!id && yearParam && reportingParam && monthParam) {
          setAnyValue(yearParam);
          setPeriodValue(monthParam);
          setPendingReporting(reportingParam);

          if (filialIdFromUrl) {
            setTipoReferencia("filial");
            setFilialTramesa(filialIdFromUrl);

            const fieldsFilial = await props.apiService.GetFilialsFromEntity(
              props.entity.lookupId,
            );
            setTramesesFilial(fieldsFilial);

            const fieldsTramesa = await props.apiService.GetTramesesTypes(
              "filialId",
              filialIdFromUrl,
            );
            setTramesesTypes(fieldsTramesa);
            setTipologiaTramesaEnable(true);
            setLoadingTypes(false);
          } else {
            setTipoReferencia("entitat");

            const fieldsTramesa = await props.apiService.GetTramesesTypes(
              "entityId",
              props.entity.lookupId,
            );

            setTramesesTypes(fieldsTramesa);
            setTipologiaTramesaEnable(true);
            setLoadingTypes(false);
          }
        }

        setLoadingPage(false);
      } catch (error) {
        console.error("Error carregant dades:", error);
        setLoadingPage(false);
        notifications.show({
          title: "Error",
          message: "Error al carregar les dades de la tramesa",
          color: "red",
        });
      }
    };

    init();
  }, [id]);

  // Effect para cargar períodos cuando cambia la tipología en modo edición
  useEffect(() => {
    if (editar && tipologiaTramesa && tramesesType.length > 0 && tramesaData) {
      handleTrameseTypeChange(tipologiaTramesa);
    }
  }, [tipologiaTramesa, tramesesType.length, editar]);

  // Effect separado para aplicar los valores iniciales después de calcular los períodos
  useEffect(() => {
    if (editar && initialPeriodValues && currentPeriodicity) {
      // Si es SP (sin periodicidad), aplicar valores inmediatamente
      if (currentPeriodicity === "SP") {
        setAnyValue(initialPeriodValues.year);
        setInitialPeriodValues(null);
      }
      // Si hay períodos calculados, aplicar ambos valores
      else if (periodoTramesa.length > 0) {
        setPeriodValue(initialPeriodValues.month);
        setAnyValue(initialPeriodValues.year);
        setInitialPeriodValues(null);
      }
    }
  }, [periodoTramesa, initialPeriodValues, editar, currentPeriodicity]);

  useEffect(() => {
    if (pendingReporting && tramesesType.length > 0) {
      const found = tramesesType.find(
        (t) => t.label.trim() === pendingReporting.trim(),
      );

      if (found) {
        setTipologiaTramesa(found.value);
        handleTrameseTypeChange(found.value);
        setLoadingTypes(false);
      }

      setPendingReporting(null);
    }
  }, [pendingReporting, tramesesType]);

  useEffect(() => {
    if (tramesaValorInicial && tramesesType.length > 0) {
      handleTrameseTypeChange(String(tramesaValorInicial));
    }
  }, [tramesesType]);

  const handleTramesReferenceChange = async (value: string | null) => {
    if (editar) return; // No permitir cambios en modo edición

    setTipologiaTramesa("");

    setGroups([]);

    if (value === "filial") {
      setTipoReferencia(value);
      setTipologiaTramesaEnable(false);
      setTramesesTypes([]);
      setLoadingFilial(true);

      try {
        const fieldsFilial = await props.apiService.GetFilialsFromEntity(
          props.entity.lookupId,
        );
        setTramesesFilial(fieldsFilial);
      } catch (error) {
        console.error("Error fetching filial data:", error);
      } finally {
        setLoadingFilial(false);
      }
    } else if (value === "entitat") {
      setTipoReferencia(value);
      setTipologiaTramesaEnable(true);
      setLoadingTypes(true);

      try {
        const fieldsTramesa = await props.apiService.GetTramesesTypes(
          "entityId",
          props.entity.lookupId,
        );
        setTramesesTypes(fieldsTramesa);
        setLoadingTypes(false);
      } catch (error) {
        console.error("Error fetching entity data:", error);
        setLoadingTypes(false);
      }
    }
  };

  const handleTramesFilialChange = async (value: string | null) => {
    if (value) {
      setFilialTramesa(value);
      setTipologiaTramesaEnable(true);
      setTramesesTypes([]);
      setLoadingTypes(true);

      try {
        const fieldsTramesa = await props.apiService.GetTramesesTypes(
          "filialId",
          value,
        );
        setTramesesTypes(fieldsTramesa);
        setLoadingTypes(false);
      } catch (error) {
        console.error("Error fetching tramesa types:", error);
        setLoadingTypes(false);
      }
    }
  };

  const handleTrameseTypeChange = async (value: string | null) => {
    if (!value) return;

    setTipologiaTramesa(value);
    try {
      const trames = tramesesType.filter((x) => x.value == value)[0];

      if (trames) {
        const tipePeriod = trames.periodicity;
        setCurrentPeriodicity(tipePeriod);

        // Generate years
        const startDate = new Date(trames.startDate);
        const startYear = startDate.getFullYear();
        const nextYear = new Date().getFullYear() + 1;
        const years = [];
        for (let year = startYear; year <= nextYear; year++) {
          years.push(year.toString());
        }
        years.reverse();
        setAnyOptions(years);

        if (tipePeriod === "SP") {
          setPeriodoTramesa([]);
        } else if (tipePeriod === "ME") {
          const allMonths = mesesTramesa.map((mes) => ({
            label: mes.mes,
            value: mes.valor.toString(),
          }));
          setPeriodoTramesa(allMonths);
        } else {
          const resultado: MesInfo[] = [];
          const periocidad = Number(
            periocoidad.find((p) => p.mes === tipePeriod)?.valor ?? 0,
          );
          const mes = parseInt(trames.initialMonth);

          for (let i = mes; i <= 12; i += periocidad) {
            const mesEncontrado = mesesTramesa.find((m) => m.valor === i);
            if (mesEncontrado) {
              resultado.push({
                label: mesEncontrado.mes,
                value: mesEncontrado.valor.toString(),
              });
            }
          }

          for (let i = mes; i >= 1; i -= periocidad) {
            const mesEncontrado = mesesTramesa.find((m) => m.valor === i);
            if (mesEncontrado) {
              resultado.push({
                label: mesEncontrado.mes,
                value: mesEncontrado.valor.toString(),
              });
            }
          }

          const periodoData = resultado.filter(
            (v, i, self) => i === self.findIndex((m) => m.value === v.value),
          );
          periodoData.sort((a, b) => parseInt(a.value) - parseInt(b.value));
          setPeriodoTramesa(periodoData);
        }

        // Solo cargar nuevo formulario si NO estamos en modo edición
        if (!editar) {
          const fieldsTramesesByTypes =
            await props.apiService.GetTramesesByTypes(value);
          setGroups(fieldsTramesesByTypes);
        }
      }
    } catch (error) {
      console.error("Error handling tramesa type change:", error);
    }
  };

  const validateDynamicFields = () => {
    const errors: string[] = [];
    let isFormValid = true;

    for (const group of groups) {
      for (const field of group.fields) {
        if (field.required) {
          const formElement = (
            formRef.current?.elements as HTMLFormControlsCollection
          )?.namedItem(field.title as string) as
            | HTMLInputElement
            | HTMLSelectElement
            | HTMLTextAreaElement
            | null;
          const value = formElement?.value?.trim();
          if (!value) {
            errors.push(`${field.title}" es obligatori`);
            isFormValid = false;
          }
        }
      }
    }

    return {
      valid: isFormValid,
      errors,
    };
  };

  const handleEnviarTramesaNew = async (isDraft?: boolean) => {
    const errors: string[] = [];
    let isFormValid = true;

    if (tipoReferencia === "") {
      errors.push("tramesa referent es obligatori");
      isFormValid = false;
    }

    if (tipologiaTramesa === "") {
      errors.push("Tipologia de tramesa es obligatori");
      isFormValid = false;
    }

    if (currentPeriodicity !== "SP" && getPeriodValue === "") {
      errors.push("Mes de referència es obligatori ");
      isFormValid = false;
    }

    if (currentPeriodicity !== "SP" && getAnyValue === "") {
      errors.push("Any  es obligatori");
      isFormValid = false;
    }

    if (!isDraft) {
      const resultFormDinamic = validateDynamicFields();
      if (!resultFormDinamic.valid) {
        isFormValid = false;
        errors.push(...resultFormDinamic.errors);
      }
    }

    if (!checkTerms) {
      errors.push(
        "Si us plau accepteu els termes establerts al avís legal i la política de privacitat.",
      );
      isFormValid = false;
    }

    if (destinatarisRef.current?.hasUnsavedInput()) {
      setUnsavedEmail(destinatarisRef.current.getUnsavedInput());
      setUnsavedModalOpened(true);
      return;
    }

    if (!isFormValid) {
      const errorMessage = errors.join("\n");
      console.error("Errores de validación:\n", errorMessage);

      notifications.show({
        title: <span style={{ fontSize: "15px" }}>Error de Validació</span>,
        message: (
          <div style={{ fontSize: "12px" }}>
            {errors.map((err, i) => (
              <div key={i}>{err}</div>
            ))}
          </div>
        ),
        color: "red",
      });

      return;
    }

    if (isFormValid) {
      try {
        setIsSubmitting(true);

        const formData = new FormData(formRef.current!);
        const updatedGroups = groups.map((group) => ({
          ...group,
          fields: group.fields.map((field) => ({
            ...field,
            value: formData.get(field.title) || field.value || "",
          })),
        }));

        const newFiles = uploadedFiles.filter(
          (file) => !oldFiles.some((old) => old.name === file.name),
        );
        const docs = await Promise.all(
          newFiles.map(async (item) => {
            const base64 = await convertToBase64(item.file);
            return {
              title: "00_" + item.name,
              content: base64.split(",")[1],
            };
          }),
        );

        const dataForm = {
          typeId: Number(tipologiaTramesa),
          referencePeriodMonth: getPeriodValue,
          referencePeriodYear: getAnyValue,
          formJson: JSON.stringify(updatedGroups),
          otherReceivers,
          comments: getComentarisValue,
          docs,
          isDraft,
          entitatId: props.entity.lookupId,
          ...(tipoReferencia === "filial" && {
            filialId: Number(filialTramesa),
          }),
        };

        if (editar) {
          const result = await props.apiService.UpdateTramesa(id, dataForm);

          if (result.statusText === "OK") {
            notifications.show({
              title: "Tramesa actualitzada",
              message: "La tramesa s'ha actualitzat correctament",
              color: "teal",
            });

            setLoadResponse(true);
            setCodi(result.data.itemGuid);
            setTramitId(result.data.itemId);

            if (!isDraft) {
              setPDF(result.data.document.fileContents);
            }
          } else {
            notifications.show({
              title: "Error",
              message: result.message || "S'ha produït un error inesperat.",
              color: "red",
            });
          }
        } else {
          const docsWithoutContent = docs.map(({ content, ...doc }) => doc);

          const dataForm = {
            typeId: Number(tipologiaTramesa),
            referencePeriodMonth: getPeriodValue,
            referencePeriodYear: getAnyValue,
            formJson: JSON.stringify(updatedGroups),
            otherReceivers,
            comments: getComentarisValue,
            docs: docsWithoutContent,
            isDraft,
            entitatId: props.entity.lookupId,
            ...(tipoReferencia === "filial" && {
              filialId: Number(filialTramesa),
            }),
          };

          const result = await props.apiService.CreateTramesa(dataForm);

          if (result.statusText === "OK") {
            const { itemId } = result.data;

            if (uploadedFiles.length > 0) {
              setUploadProgress({ done: 0, total: uploadedFiles.length });
              try {
                const CONCURRENCY = 5;
                for (let i = 0; i < uploadedFiles.length; i += CONCURRENCY) {
                  const batch = uploadedFiles.slice(i, i + CONCURRENCY);
                  await Promise.all(
                    batch.map((item) =>
                      uploadFileInChunks(item.file, itemId, "Trameses", props.apiService).then(() => {
                        setUploadProgress((prev) => prev ? { ...prev, done: prev.done + 1 } : prev);
                      }),
                    ),
                  );
                }
              } finally {
                setUploadProgress(null);
              }
            }

            notifications.show({
              title: isDraft ? "Esborrany desat" : "Tramesa enviada",
              message: isDraft
                ? "L'esborrany s'ha desat correctament"
                : "La tramesa s'ha enviat correctament",
              color: "teal",
            });

            setLoadResponse(true);
            setCodi(result.data.itemGuid);
            setTramitId(result.data.itemId);

            if (!isDraft) {
              setPDF(result.data.document.fileContents);
            }
          } else {
            notifications.show({
              title: "Error",
              message: result.message || "S'ha produït un error inesperat.",
              color: "red",
            });
          }
        }
      } catch (error: any) {
        console.error("Error enviando tramesa:", error);
        notifications.show({
          title: "Error",
          message:
            error.message || "S'ha produït un error en enviar la tramesa.",
          color: "red",
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <PageWithBreadcrumbs onBreadcrumbNavigate={handleBreadcrumbNavigation}>
      {loadResponsePDF ? (
        <>
          <Title order={1}>Acusament de recepció</Title>
          <ContainerWhite>
            <Box bg="brand.0" p="md" style={{ borderRadius: "8px" }}>
              <Group
                gap="xl"
                align="flex-start"
                justify="space-between"
                style={{ width: "100%" }}
              >
                <div style={{ flex: 1 }}>
                  {pdf ? (
                    <AfaFieldDisplay label="La tramesa d'informació s'ha realizat correctament" />
                  ) : (
                    <AfaFieldDisplay label="L'esborrany s'ha desat correctament" />
                  )}

                  <Text size="md" fw={400}>
                    Codi de seguiment: {codi}
                  </Text>
                </div>
              </Group>
              <div style={{ marginTop: "20px" }}>
                {pdf && (
                  <iframe
                    src={`data:application/pdf;base64,${pdf}`}
                    width="100%"
                    height="600"
                  />
                )}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <AfaButton
                  id="btn-tornar"
                  variant="outline"
                  onClick={() => navigate("/trameses/consultar")}
                >
                  Tornar
                </AfaButton>
                <AfaButton
                  id="btn-detall"
                  onClick={() => navigate("/trameses/detall/" + tramitId)}
                >
                  Anar al detall
                </AfaButton>
              </div>
            </Box>
          </ContainerWhite>
        </>
      ) : (
        <>
          <Title order={1}>{titleInit}</Title>
          <ContainerWhite>
            <Box bg="brand.0" p="md" style={{ borderRadius: "8px" }}>
              <Group gap="xl" align="flex-start">
                <AfaFieldDisplay
                  label="Entitat"
                  value={props.entity != null ? props.entity.lookupValue : ""}
                  style={{ flex: 1 }}
                />
                <AfaFieldDisplay
                  label="Informació d'interès"
                  button={{
                    text: "Consultar informació",
                    onClick: () => setModalOpened(true),
                    id: "btn-consultar-info",
                  }}
                  style={{ flex: 1 }}
                />
              </Group>
            </Box>

            {loadingPage ? (
              <AfaPageLoader message="Carregant formulari..." />
            ) : (
              <>
                <Box mt={"md"}>
                  <Stack gap="md">
                    <Group align="flex-start">
                      <div style={{ flex: 1 }}>
                        <Radio.Group
                          name="tipoReferencia"
                          label="Vull iniciar una tramesa referent a una"
                          value={tipoReferencia}
                          onChange={handleTramesReferenceChange}
                          required
                        >
                          <Group mt="xs">
                            <Radio
                              disabled={editar}
                              value="entitat"
                              label="Entitat"
                            />
                            <Radio
                              disabled={editar}
                              value="filial"
                              label="Filial"
                            />
                          </Group>
                        </Radio.Group>
                      </div>

                      <div style={{ flex: 1 }}>
                        {tipoReferencia === "filial" && (
                          <AfaCombobox
                            name="filial"
                            id="filial"
                            label="Filial"
                            placeholder="Seleccioni una filial"
                            data={tramesesFilial}
                            value={filialTramesa}
                            required
                            disabled={editar || loadingFilial}
                            loading={loadingFilial}
                            onChange={handleTramesFilialChange}
                          />
                        )}
                      </div>
                    </Group>

                    <Group align="flex-start">
                      <div style={{ flex: 1 }}>
                        <AfaCombobox
                          name="tipologiaTramesa"
                          id="tipologia-tramesa"
                          label="Tipologia de tramesa"
                          placeholder="Seleccioni una opció"
                          data={tramesesType}
                          value={tipologiaTramesa}
                          onChange={handleTrameseTypeChange}
                          disabled={
                            !tipologiaTramesaEnable || loadingTypes || editar
                          }
                          loading={tipologiaTramesaEnable && loadingTypes}
                          required
                        />
                      </div>
                      <div style={{ flex: 1 }}></div>
                    </Group>

                    {tipologiaTramesa !== "" && (
                      <Group align="flex-end">
                        <div style={{ flex: 1 }}>
                          <DestinatarisEditable
                            ref={destinatarisRef}
                            value={otherReceivers}
                            onChange={setOtherReceivers}
                          />
                        </div>

                        <div style={{ flex: 1 }}>
                          {currentPeriodicity !== "SP" && (
                            <Group>
                              <div style={{ flex: 1 }}>
                                <AfaSelect
                                  name="periodeReferencia"
                                  id="periode-referencia"
                                  label="Mes de referència"
                                  placeholder="Seleccioni una opció"
                                  data={periodoTramesa}
                                  onChange={(value) =>
                                    setPeriodValue(value || "")
                                  }
                                  required
                                  value={getPeriodValue}
                                />
                              </div>

                              <div style={{ flex: 1 }}>
                                <AfaSelect
                                  name="any"
                                  id="any"
                                  label="Any"
                                  placeholder="Seleccioni un any"
                                  data={anyOptions}
                                  onChange={(value) => setAnyValue(value || "")}
                                  required
                                  value={getAnyValue}
                                />
                              </div>
                            </Group>
                          )}
                        </div>
                      </Group>
                    )}

                    {tipologiaTramesa !== "" && (
                      <Group align="flex-start">
                        <div style={{ flex: 1 }}>
                          <AfaTextarea
                            id="comentaris"
                            name="comentaris"
                            label="Comentaris"
                            placeholder="Escriure aquí"
                            autosize
                            minRows={3}
                            maxRows={6}
                            value={getComentarisValue}
                            onChange={(e) => setComentarisValue(e.target.value)}
                          />
                        </div>
                        <div style={{ flex: 1 }}></div>
                      </Group>
                    )}

                    <form ref={formRef}>
                      {groups.length > 0 && (
                        <>
                          <Title order={3}>Formulari</Title>
                          <hr />
                        </>
                      )}
                      <Group align="flex-start">
                        <div style={{ flex: 1 }}>
                          {groups.map((group, groupIndex) => (
                            <div
                              key={groupIndex}
                              style={{ marginBottom: "24px" }}
                            >
                              <Title order={3}>{group.groupName}</Title>
                              <Box mt="xs">
                                <Stack gap="xs">
                                  <Grid gutter="md">
                                    {group.fields.map((field, index) => {
                                      let span = 12;
                                      if (field.colsNumber === 2) span = 6;
                                      if (field.colsNumber === 3) span = 4;

                                      return (
                                        <Grid.Col key={index} span={span}>
                                          {field.fieldType === "Text" && (
                                            <AfaTextInput
                                              id={`text_${groupIndex}_${index}`}
                                              name={field.title}
                                              placeholder="Insert"
                                              label={field.title}
                                              type="text"
                                              required={field.required}
                                              defaultValue={field.value || ""}
                                            />
                                          )}

                                          {field.fieldType === "Select" && (
                                            <AfaSelect
                                              id={`select_${groupIndex}_${index}`}
                                              name={field.title}
                                              label={field.title}
                                              placeholder="Seleccioni"
                                              data={field.options ?? []}
                                              required={field.required}
                                              defaultValue={field.value || ""}
                                            />
                                          )}

                                          {field.fieldType ===
                                            "Multiple Text" && (
                                            <AfaTextarea
                                              id={`multi_${groupIndex}_${index}`}
                                              name={field.title}
                                              label={field.title}
                                              placeholder="Escriure aquí"
                                              autosize
                                              minRows={3}
                                              maxRows={6}
                                              required={field.required}
                                              defaultValue={field.value || ""}
                                            />
                                          )}

                                          {field.fieldType === "DateTime" && (
                                            <AfaDatePickerInput
                                              id={`date_${groupIndex}_${index}`}
                                              name={field.title}
                                              label={field.title}
                                              required={field.required}
                                              defaultValue={
                                                field.value
                                                  ? new Date(
                                                      `${field.value}T00:00:00`,
                                                    )
                                                  : null
                                              }
                                            />
                                          )}
                                        </Grid.Col>
                                      );
                                    })}
                                  </Grid>
                                </Stack>
                              </Box>
                            </div>
                          ))}
                        </div>
                      </Group>
                    </form>
                  </Stack>
                </Box>

                {tipologiaTramesa !== "" && (
                  <>
                    <Title order={3} mt={"xl"}>
                      Documents
                    </Title>
                    <Box mt="xs">
                      <AfaDropzone
                        onDrop={(files) => {
                          const newFiles = files.map((file) => {
                            //const extension = file.name.split('.').pop()?.toLowerCase();
                            const extension = getFileExtension(file.name);
                            let type:
                              | "pdf"
                              | "doc"
                              | "xls"
                              | "ppt"
                              | "png"
                              | "jpg"
                              | "other" = "other";

                            if (extension === "pdf") type = "pdf";
                            else if (
                              extension === "doc" ||
                              extension === "docx"
                            )
                              type = "doc";
                            else if (
                              extension === "xls" ||
                              extension === "xlsx"
                            )
                              type = "xls";
                            else if (
                              extension === "ppt" ||
                              extension === "pptx"
                            )
                              type = "ppt";
                            else if (extension === "png") type = "png";
                            else if (
                              extension === "jpg" ||
                              extension === "jpeg"
                            )
                              type = "jpg";

                            return { file, name: file.name, type };
                          });

                          setUploadedFiles((prevFiles) => [
                            ...prevFiles,
                            ...newFiles,
                          ]);
                        }}
                        uploadedFiles={uploadedFiles}
                        onDeleteFile={(_, index) => {
                          const newFiles = uploadedFiles.filter(
                            (_, i) => i !== index,
                          );
                          setUploadedFiles(newFiles);

                          const fileToDelete = uploadedFiles[index];
                          if (!fileToDelete) return;
                          setDeletedFiles((prev) => [
                            ...prev,
                            fileToDelete.name,
                          ]);
                        }}
                      />
                    </Box>
                  </>
                )}
              </>
            )}

            <Box mt="xl">
              <AfaCheckbox
                onChange={(event) => setCheckTerms(event.currentTarget.checked)}
                label={
                  <>
                    Accepto els termes establerts en l'
                    <AfaAnchor
                      target="_blank"
                      href="https://www.afa.ad/ca/avis-legal"
                      size="md"
                    >
                      Avís legal
                    </AfaAnchor>{" "}
                    i la{" "}
                    <AfaAnchor
                      target="_blank"
                      href="https://www.afa.ad/ca/politica-de-privacitat"
                      size="md"
                    >
                      Política de privacitat
                    </AfaAnchor>{" "}
                    <span style={{ color: "var(--mantine-color-red-6)" }}>
                      *
                    </span>
                  </>
                }
              />
            </Box>
          </ContainerWhite>

          <Group justify="space-between" mt="xs">
            <AfaButton
              id="btn-cancelar"
              variant="outline"
              onClick={() => {
                setPendingNavigation(null);
                setCancelModalOpened(true);
              }}
              disabled={isSubmitting}
            >
              Cancel·lar
            </AfaButton>

            <Group gap="md">
              <AfaButton
                id="btn-desa-esborrany"
                variant="outline"
                onClick={() => handleEnviarTramesaNew(true)}
                disabled={isSubmitting}
                loading={isSubmitting}
                loaderProps={{ type: "dots" }}
              >
                {isSubmitting ? "Desant..." : "Desa com a esborrany"}
              </AfaButton>

              <AfaButton
                id="btn-enviar-ara"
                variant="filled"
                onClick={() => handleEnviarTramesaNew()}
                disabled={isSubmitting}
                loading={isSubmitting}
                loaderProps={{ type: "dots" }}
              >
                {isSubmitting ? "Enviant..." : "Enviar ara"}
              </AfaButton>
            </Group>
          </Group>

          <AfaModal
            opened={uploadProgress !== null}
            onClose={() => {}}
            title="Pujant documents..."
            size="sm"
            centered
            withCloseButton={false}
            closeOnClickOutside={false}
            closeOnEscape={false}
          >
            <Stack gap="md" py="sm">
              <Text size="sm" c="dimmed" ta="center">
                {uploadProgress
                  ? `Document ${uploadProgress.done} de ${uploadProgress.total} pujat correctament`
                  : "Preparant la pujada..."}
              </Text>
              <Progress
                value={uploadProgress ? (uploadProgress.done / uploadProgress.total) * 100 : 0}
                animated
                color="teal"
                size="lg"
              />
              <Text size="xs" c="dimmed" ta="center">
                Si us plau, no tanqueu aquesta pàgina.
              </Text>
            </Stack>
          </AfaModal>

          <AfaModal
            opened={modalOpened}
            onClose={() => setModalOpened(false)}
            title="Informació d'interès"
            size="lg"
            centered
          >
            <Stack gap="md" mt="md">
              {infoInteres.map((item, idx) => (
                <AfaAnchor target="_blank" key={idx} href={item.link} size="md">
                  {item.title}
                </AfaAnchor>
              ))}
              <Stack>
                <Text size="md" fw={400}>
                  Per a qualsevol incidència tècnica o relacionada amb els
                  processos, si us plau, contacteu a{" "}
                  <AfaAnchor href="mailto:reporting@afa.ad" size="md">
                    reporting@afa.ad
                  </AfaAnchor>
                </Text>
              </Stack>
            </Stack>
          </AfaModal>

          <AfaModal
            opened={cancelModalOpened}
            onClose={() => {
              setCancelModalOpened(false);
              setPendingNavigation(null);
            }}
            title="Cancel·lar tramesa"
            size="md"
            centered
          >
            <Stack mt={"sm"}>
              <div style={{ width: "100%" }}>
                <AfaAlert
                  type="warning"
                  variant="light"
                  title="Cancel·lar tramesa"
                >
                  Si cancel·les aquesta tramesa, es perdran totes les dades
                  introduïdes. Aquesta acció no es pot desfer.
                </AfaAlert>
              </div>
            </Stack>

            <Group justify="flex-end" mt="md">
              <AfaButton
                id="btn-cancelar-tramesa"
                variant="filled"
                color="red"
                onClick={() => {
                  setCancelModalOpened(false);

                  if (pendingNavigation) {
                    window.location.href = pendingNavigation;
                    setPendingNavigation(null);
                  } else {
                    navigate("/trameses/consultar");
                  }
                }}
              >
                Cancel·lar tramesa
              </AfaButton>
            </Group>
          </AfaModal>
          <AfaModal
            opened={unsavedModalOpened}
            onClose={() => setUnsavedModalOpened(false)}
            title="Destinatari sense afegir"
            size="md"
            centered
          >
            <AfaAlert type="warning" variant="light" title="Atenció">
              Tens un destinatari sense afegir: <strong>{unsavedEmail}</strong>
              <br />
              Si us plau, afegeix-lo o esborra'l abans d'enviar la tramesa.
            </AfaAlert>
            <Group justify="flex-end" mt="md">
              <AfaButton onClick={() => setUnsavedModalOpened(false)}>
                D'acord
              </AfaButton>
            </Group>
          </AfaModal>
        </>
      )}
    </PageWithBreadcrumbs>
  );
}
